/**
 *
 * Update Account
 *
 */

import React, { memo, useEffect, useReducer } from 'react';

import { CloudProviders } from '@ariksa/cloud-account/api';
import {
  Box,
  Center,
  HStack,
  ListItem,
  OrderedList,
  RadioGroup,
  Stack,
} from '@chakra-ui/react';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { customTheme } from 'theme';

import {
  AzureActiveDirectoryIcon,
  AzureIcon,
  ExclamationTriangleIcon,
  Form,
} from 'app/components';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { ReadOnlyTag } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/utils/ReadOnlyTag';
import { renderRadioButton } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/utils/utils';
import { selectCloudAccountWizard } from 'containers/Setup/CloudAccounts/CloudAccountWizard/selectors';
import { actions } from 'containers/Setup/CloudAccounts/CloudAccountWizard/slice';
import { selectSetup } from 'containers/Setup/selectors';
import { actions as setupActions } from 'containers/Setup/slice';

import { selectCloudAccounts } from '../../../selectors';

interface Props {
  provider: CloudProviders;
}

export const EditAzureAccount = memo((props: Props) => {
  const { provider } = props;
  const dispatch = useDispatch();
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accountDetails } = useSelector(selectCloudAccounts);
  const { onboardAccount } = useSelector(selectSetup);
  const { subscriptions } = useSelector(selectCloudAccountWizard);
  const { data, isLoading } = accountDetails;

  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      name: '',
      accountNameUsedError: '',
      subscriptionId: '',
      applicationId: '',
      tenantId: '',
      secret: '',
      updateAllAccounts: true,
      isUpdated: false,
    },
  );

  useEffect(() => {
    dispatch(
      actions.getAccountsByProviderConfig({
        q: {
          cloudAccountSearchFilter: {
            cloud_type: CloudProviders.Azure,
            config_field: 'client_id',
            config_value: data?.azure?.client_id ?? '',
          },
        },
      }),
    );
  }, [dispatch, data]);

  useEffect(() => {
    updateState({
      name: data.name,
      subscriptionId: data?.azure?.subscription_id,
      applicationId: data?.azure?.client_id ?? '',
      tenantId: data?.azure?.authority ?? '',
      secret: data?.azure?.client_credential ?? '',
    });
  }, [data]);

  const handleSubmit = data => {
    const { account_name } = data;

    dispatch(
      setupActions.updateCloudAccount({
        q: {
          uuid: params.id!,
          cloudAccountUpdate: {
            cloud_type: props.provider,
            name: account_name,
            ...(provider === CloudProviders.Azure
              ? {
                  azure: {
                    client_credential: !!state.secret
                      ? state.secret
                      : undefined,
                    update_all_accounts:
                      state.updateAllAccounts && !!state.secret,
                  },
                }
              : {}),
          },
        },
        onSuccess: () => {
          navigate('/setup/accounts');
        },
      }),
    );
  };

  const handleCancel = () => {
    navigate('/setup/accounts');
  };

  const boxStyles = {
    border: '1px solid',
    borderRadius: 6,
    borderColor: 'componentBorder',
    p: 6,
  };

  return (
    <Box w="full">
      <Form
        title={
          provider === CloudProviders.Azure
            ? 'Edit Microsoft Azure'
            : 'Update AzureAD'
        }
        titleIcon={
          provider === CloudProviders.Azure ? (
            <AzureIcon />
          ) : (
            <AzureActiveDirectoryIcon />
          )
        }
        styles={{ form: { formWidth: '750px', title: { iconFilled: false } } }}
        schema={{
          account_name: {
            type: 'text',
            label: 'Account Name',
            placeholder: 'Engineering-US',
            isRequired: true,
            value: state.name,
            onChange: value =>
              updateState({ name: value, isUpdated: data.name !== value }),
          },
          ...(provider === CloudProviders.Azure
            ? {
                cfs_template_type_info: {
                  type: 'custom-with-form-control',
                  label: 'Onboarding mode',
                  component: <ReadOnlyTag />,
                },
                subscriptionId: {
                  type: 'text',
                  label: 'Subscription ID',
                  value: state.subscriptionId,
                  isRequired: true,
                  isDisabled: true,
                },
                tenantId: {
                  type: 'text',
                  label: 'Directory (Tenant) ID',
                  value: state.tenantId,
                  isRequired: true,
                  isDisabled: true,
                },
                applicationId: {
                  type: 'text',
                  label: 'Application (Client) ID',
                  value: state.applicationId,
                  isRequired: true,
                  isDisabled: true,
                },
                secret: {
                  type: 'text',
                  label: 'Secret',
                  placeholder: '******************',
                  value: state.secret,
                  htmlInputType: 'password',
                  onChange: value =>
                    updateState({
                      secret: value,
                      isUpdated: true,
                    }),
                },
                ...(!!state.secret
                  ? {
                      updateAllAccounts: {
                        type: 'custom',
                        component: () => (
                          <Box {...boxStyles} bg={customTheme.colors.blue[50]}>
                            <Stack>
                              <HStack spacing={6} align="flex-start">
                                <Box boxSize={8} color="red">
                                  <Center>
                                    <ExclamationTriangleIcon />
                                  </Center>
                                </Box>
                                <Stack flex={1} spacing={3}>
                                  <Box color={customTheme.colors.gray['300']}>
                                    There{' '}
                                    {subscriptions.data?.length === 1
                                      ? 'is '
                                      : 'are '}
                                    {subscriptions?.isLoading ? (
                                      <Box display="inline-block">
                                        <CustomSpinner />
                                      </Box>
                                    ) : (
                                      subscriptions?.data?.length
                                    )}{' '}
                                    subscription
                                    {subscriptions.data?.length === 1
                                      ? ''
                                      : 's'}{' '}
                                    that
                                    {subscriptions.data?.length === 1
                                      ? ' has'
                                      : ' have'}{' '}
                                    the same Application (Client) ID. Please
                                    choose an option below based on whether all
                                    these subscriptions also use the same
                                    secret.
                                  </Box>
                                  {subscriptions.isLoading ? (
                                    <Center h={32}>
                                      <CustomSpinner />
                                    </Center>
                                  ) : (
                                    <OrderedList gap={3}>
                                      {map(subscriptions.data, o => (
                                        <ListItem
                                          pl={4}
                                          color={customTheme.colors.gray['300']}
                                        >
                                          {o.cloud_account_id}
                                        </ListItem>
                                      ))}
                                    </OrderedList>
                                  )}
                                  <RadioGroup
                                    value={
                                      state.updateAllAccounts
                                        ? 'all'
                                        : 'individual'
                                    }
                                    pt={8}
                                    onChange={val =>
                                      updateState({
                                        updateAllAccounts: val === 'all',
                                        isUpdated: true,
                                      })
                                    }
                                  >
                                    <Stack spacing={6}>
                                      {renderRadioButton(
                                        'Update the secret for all the subscriptions noted above',
                                        'I have verified that this secret is shared by all subscriptions above',
                                        'all',
                                        'black',
                                      )}
                                      {renderRadioButton(
                                        'I’d prefer to update each subscription that uses this secret individually',
                                        'I understand that there may temporary disruption as long as secrets are not updated',
                                        'individual',
                                        'black',
                                      )}
                                    </Stack>
                                  </RadioGroup>
                                </Stack>
                              </HStack>
                            </Stack>
                          </Box>
                        ),
                      },
                    }
                  : {}),
                alert: {
                  type: 'custom',
                  component: () => (
                    <Box {...boxStyles} bg="#fdf8e9">
                      <HStack spacing={6}>
                        <Box boxSize={8} color="red">
                          <Center>
                            <ExclamationTriangleIcon />
                          </Center>
                        </Box>
                        <Box flex={1}>
                          If Ariksa is unable to authorize using the updated
                          secret, we will mark the subscription as NOT
                          AUTHORIZED under Accounts.
                        </Box>
                      </HStack>
                    </Box>
                  ),
                },
              }
            : {}),
        }}
        buttonOptions={{
          submit: {
            name: 'Update',
            isLoading: isLoading || onboardAccount.isLoading,
            isDisabled: !state.isUpdated || !state.name,
          },
          reset: {
            name: 'Cancel',
            isVisible: true,
            onClick: handleCancel,
          },
        }}
        isLoading={isLoading}
        handleSubmit={handleSubmit}
      />
    </Box>
  );
});
